<template>
	<article class="weather-forecast">
		<!-- ローディング -->
		<transition name="fade">
			<webview-redraw-progress v-if="isLoading" />
		</transition>
		<!-- /ローディング -->

		<!-- エラー -->
		<div v-if="isError" class="weather-forecast__content weather-forecast__error">
			<p class="weather-forecast__error--bg"><img src="~@/assets/img/weather_forecast_bg.svg"></p>
			<common-error-message>データを正常に<br>取得できませんでした</common-error-message>
		</div>
		<!-- /エラー -->

		<!-- 天気予報 -->
		<div v-else class='weather-forecast__content' id="js_weatherForecast">
			<div class='weather-forecast__inner' id="js_weatherForecastInner">
				<!-- 日単位 -->
				<aside
					:class="[
						'weather-forecast-day',
						`is-view-date-type${viewDateType}`
					]"
					id="js_weatherForecastDay"
				>
					<section
						v-for="(item, index) in computedWeatherForecastData" :key="`day-${index}`"
						:class="[
							'weather-forecast-day__inner',
							`is-${item.type}`,
							(viewDateType === 0 && index === 0) || (viewDateType === 1 && index === 1) || (viewDateType === 2 && index === 1) || (viewDateType === 3 && index === 2) ? 'is-active' : 'is-passive'
						]"
						:id="`js_${item.type}`"
						@click="onChangeDate($event, item.type)"
					>
						<h2 class="weather-forecast-day__title">
							<span class="weather-forecast-day__title--fixed">{{ item.date_ja }}</span>
							<span class="weather-forecast-day__title--variable">
								<span class="is-date">{{ item.date }}(<span :class="item.week_day_type">{{ item.week_day }}</span>)</span>
							</span>
						</h2>
						<div class="weather-forecast-day__weather">
							<div class="weather-forecast-day__icon">
								<i :class="`sprite_weather_forcast_day tenki_${item.tenki_id}`" />
							</div>
							<div class="weather-forecast-day__forecast">
								<p class="weather-forecast-day__forecast--tenki is-hide">{{ item.tenki_ja }}</p>
								<div class="weather-forecast-day__forecast--temp">
									<p class="is-temp-max">
										<span class="is-large">{{ item.hight_temp }}</span>&#8451;<span class="is-hide">[{{ item.hight_temp_diff }}]</span>
									</p>
									<p class="is-temp-min">
										<span class="is-large">{{ item.low_temp }}</span>&#8451;<span class="is-hide">[{{ item.low_temp_diff }}]</span>
									</p>
								</div>
								<p class="weather-forecast-day__forecast--precipitation">
									<img src="~@/assets/img/rain.svg" />
									<span class="is-large">{{ item.rainy_percent }}</span>&#37;
								</p>
							</div>
						</div>
						<div class="weather-forecast-day__pressure">
							<i :class="['sprite_weather_forcast_day', `pressure_icon_lv${item.pressure_level}`]" />
						</div>
					</section>
				</aside>
				<!-- /日単位 -->

				<!-- 時単位 -->
				<aside class="weather-forecast-hour">
					<!-- スクロールバー -->
					<nav class="weather-forecast-hour__scroll-bar">
						<i class="weather-forecast-hour__scroll-bar--point" id="js_weatherForecastHour_scrollPointer" />
					</nav>
					<!-- /スクロールバー -->

					<div
						:class="[
							'weather-forecast-hour__inner',
							{ 'is-close': isAccordionClose }
						]"
						id="js_weatherForecastHour"
					>
						<ul class="weather-forecast-hour__header">
							<li class="weather-forecast-hour__header--time">
								<i class="sprite_weather_forcast_hour icon_hour" />
								<p>時刻</p>
							</li>
							<li class="weather-forecast-hour__header--temperature">
								<i class="sprite_weather_forcast_hour icon_temperature" />
								<p>気温</p>
							</li>
							<li class="weather-forecast-hour__header--pressure">
								<i class="sprite_weather_forcast_hour icon_pressure" />
								<p>気圧<br>(hPa)</p>
							</li>
							<li class="weather-forecast-hour__header--rainy">
								<i class="sprite_weather_forcast_hour icon_rain" />
								<p>降水</p>
							</li>
							<li class="weather-forecast-hour__header--humidity">
								<i class="sprite_weather_forcast_hour icon_humidity" />
								<p>湿度</p>
							</li>
							<li class="weather-forecast-hour__header--wind">
								<i class="sprite_weather_forcast_hour icon_wind" />
								<p>風</p>
							</li>
						</ul>
						<div class="weather-forecast-hour__data">
							<section
								v-for="(item, indexDay) in computedWeatherData"
								:key="`weather-forecast-hour-${indexDay}`"
								:class="[
									'weather-forecast-hour__data--inner',
									`weather-forecast-hour__data--${item.type}`
								]"
							>
								<h2 class="weather-forecast-hour__data--date" :id="`js_${item.type}Hour-title`">{{ item.date_ja }}</h2>
								<div
									v-for="data in item.data"
									:key="`${item.type}-${data.hour}`"
									:id="`js_${item.type}Hour-${data.hour}`"
									class="weather-forecast-hour__data--container"
								>
									<ul class="weather-forecast-hour__data--item">
										<li
											:class="[
												'weather-forecast-hour__data--time',
												{ 'is-now-hour': data.is_now_hour }
											]">
											<p>{{ data.hour }}</p>
										</li>
										<li class="weather-forecast-hour__data--temperature">
											<i :class="`sprite_weather_forcast_hour wicon_${data.weather_code}`" />
											<p>{{ data.temperature }}<span>&#8451;</span></p>
										</li>
										<li class="weather-forecast-hour__data--pressure">
											<i :class="`sprite_weather_forcast_hour pressure_icon_lv${data.pressure_level}`" />
											<p :class="`is-pressure-lv${data.pressure_level}`">{{ data.pressure }}</p>
										</li>
										<li class="weather-forecast-hour__data--rainy">
											<p class="is-rain_chance" :style="data.rain_chance !== '-' ? `background: linear-gradient(to top, #CEF2FF 0% ${data.rain_chance}, transparent ${data.rain_chance} 100%);` : ''"><span>{{ data.rain_chance }}</span></p>
											<p>{{ data.rain_fall }}mm</p>
										</li>
										<li class="weather-forecast-hour__data--humidity">
											<p>{{ data.humidity }}</p>
										</li>
										<li class="weather-forecast-hour__data--wind">
											<i :class="`sprite_weather_forcast_hour wind_arrow_lv${data.wind_level}`" :style="`transform: rotate(${data.wind_rotation}deg);`" />
											<p>{{ data.wind_d }}</p>
											<p>{{ data.wind_v }}m</p>
										</li>
									</ul>
									<ul v-if="arraySolarTime && arraySolarTime[indexDay].sunrise_hour === data.hour" class="weather-forecast-hour__data--item">
										<li class="weather-forecast-hour__data--time">
											<p>{{ arraySolarTime[indexDay].sunrise }}</p>
										</li>
										<li class="weather-forecast-hour__data--solor-time">
											<i class="sprite_weather_forcast_hour wicon_sunrise" />
											<p>日の出</p>
										</li>
									</ul>
									<ul v-if="arraySolarTime && arraySolarTime[indexDay].sunset_hour === data.hour" class="weather-forecast-hour__data--item">
										<li class="weather-forecast-hour__data--time">
											<p>{{ arraySolarTime[indexDay].sunset }}</p>
										</li>
										<li class="weather-forecast-hour__data--solor-time">
											<i class="sprite_weather_forcast_hour wicon_sunset" />
											<p>日の入</p>
										</li>
									</ul>
								</div>
							</section>
						</div>
					</div>
					<div class="weather-forecast-hour__toggle">
						<button class="weather-forecast-hour__toggle--button" @click="onToggle" id="trackingWebviewWeatherForecast_time">
							<v-ons-icon :icon="isAccordionClose ? 'md-chevron-down' : 'md-chevron-up'" />
						</button>
					</div>
				</aside>
				<!-- /時単位 -->
			</div>
		</div>
		<!-- /天気予報 -->
	</article>
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_WEATHER_DATA } from '@/store/modules/weatherGraph/mutation-types'
import { SET_WEATHER_FORECAST, SET_SOLAR_TIME } from '@/store/modules/weatherForecast/mutation-types'

// Compornents
import WebviewRedrawProgress from '@/components/Molecules/WebviewRedrawProgress'
import CommonErrorMessage from '@/components/Molecules/CommonErrorMessage'

// Utils
// import MunicipalCodeUtil from '@/utils/municipal-code-util'

// ライブラリ
import { scroller } from 'vue-scrollto/src/scrollTo'
import moment from 'moment'
moment.locale('ja')

export default {
	name: 'WeatherForecast',
	components: {
		WebviewRedrawProgress,
		CommonErrorMessage
	},
	data () {
		return {
			viewDateType: 0,
			$elemWrapper: null,
			$elemDay: null,
			$elemHour: null,
			$elemScrollPointer: null,
			startX: 0,
			endX: 0,
			widthWeatherForecastHour: 0,
			posToday: 0,
			posTomorrow: 0,
			posDayAfterTomorrow: 0,
			zoom: 1,
			scrollerDay: null,
			scrollerHour: null,
			arraySolarTime: null,
			isLoading: false,
			isError: false,
			isChangeDate: false,
			isSwipe: false,
			isAutoScroll: false,
			isAccordionClose: true
		}
	},
	props: {
		cityCode: {
			type: String,
			default: ''
		}
	},
	mounted () {
		// データの取得
		this.getWeatherData()
	},
	computed: {
		// map Vuex getters
		...mapGetters('common', ['userId', 'userToken', 'userData']),
		...mapGetters('weatherGraph', ['weatherData']),
		...mapGetters('weatherForecast', ['weatherForecastData']),

		// 日単位天気のデータを扱いやすいよう生成
		computedWeatherForecastData () {
			return this.weatherForecastData ? this.weatherForecastData.records.map((item, index) => {
				// 気圧レベルのコンバート
				const pressureLevel = item.zutu_level_day === '0' ? 1 : item.zutu_level_day

				// 気温前日比のコンバート
				const hightTempDiff = Math.sign(item.max_temp_diff) === -1 ? String(Math.round(item.max_temp_diff)) : `+${Math.round(item.max_temp_diff)}`
				const lowTempDiff = Math.sign(item.min_temp_diff) === -1 ? String(Math.round(item.min_temp_diff)) : `+${Math.round(item.min_temp_diff)}`

				const year = Number(item.date.substring(0, 4))
				const month = Number(item.date.substring(4, 6)) - 1
				const day = Number(item.date.substring(6, 8))
				const date = moment().year(year).month(month).date(day)

				// NOTE: アイコンが同じなので「〇一時〇」の天気コードを「〇時々〇」に書き換える
				let tenkiId = item.day_tenki_comment
				switch (tenkiId) {
					case '102':	// 晴れ 一時 雨
						tenkiId = '103'
						break
					case '104':	// 晴れ 一時 雪
						tenkiId = '105'
						break
					case '202':	// くもり 一時 雨
						tenkiId = '203'
						break
					case '204':	// くもり 一時 雪
						tenkiId = '205'
						break
					case '309':	// 雨 一時 雪
						tenkiId = '303'
						break
					case '409':	// 雪 一時 雨
						tenkiId = '403'
						break
				}

				return {
					'type': index === 0 ? 'today' : index === 1 ? 'tomorrow' : 'dayaftertomorrow',
					'date_ja': index === 0 ? '今日' : index === 1 ? '明日' : '明後日',
					'date': date.format('M/D'),
					'week_day': date.format('ddd'),
					'week_day_type': date.format('d') === '0' ? 'is-sunday' : date.format('d') === '6' ? 'is-saturday' : '',
					'tenki_id': tenkiId,
					'tenki_ja': item.day_tenki,
					'hight_temp': item.hight_temp,
					'hight_temp_diff': hightTempDiff,
					'low_temp': item.low_temp,
					'low_temp_diff': lowTempDiff,
					'rainy_percent': item.day_pre,
					'pressure_level': pressureLevel
				}
			}) : []
		},

		// 時単位データを扱いやすいよう生成
		computedWeatherData () {
			const data = [
				{ 'date_ja': '今日', 'type': 'today', 'data': [] },
				{ 'date_ja': '明日', 'type': 'tomorrow', 'data': [] },
				{ 'date_ja': '明後日', 'type': 'dayaftertomorrow', 'data': [] }
			]
			const nowDate = moment().set({ 'hour': 0, 'minute': 0, 'second': 0, 'millisecond': 0 })
			const nowHour = moment().hour()

			if (this.weatherData && this.weatherData.records) {
				for (let i = 0, len = this.weatherData.records.length; i < len; i++) {
					const item = this.weatherData.records[i]

					// 日付の取得
					const date = moment.unix(item.hour)

					// 時間のコンバート
					const hour = moment.unix(item.hour).hour()

					// 現在時刻フラグの設定
					const isNowHour = nowDate.date() === date.date() && nowHour === hour

					// 過去時刻フラグの設定
					// CHANGE: 過去分は非表示。API呼び出しで現在時刻からのデータを呼び出すように変更したのでコメントアウト
					// const isPastHour = nowDate.date()=== date.date() && hour < nowHour

					// 天気アイコンのコンバート（仕様は気圧グラフから流用）
					const hareMaxFlg = item.temperature >= 35
					const hareMoonFlg = (hour >= 18 || hour <= 3)
					const weatherCode = item.weather_code
					let weatherName = ''
					switch (true) {
						case weatherCode === '1' && hareMaxFlg === false && hareMoonFlg === false:
							weatherName = 'hare'
							break
						case weatherCode === '1' && hareMaxFlg === true && hareMoonFlg === false:
							weatherName = 'haremax'
							break
						case weatherCode === '1' && hareMoonFlg === true:
							weatherName = 'haremoon'
							break
						case weatherCode === '2':
							weatherName = 'kumori'
							break
						case weatherCode === '3':
							weatherName = 'ame'
							break
						default:
							weatherName = 'yuki'
							break
					}

					// 気圧レベルのコンバート
					const pressureLevel = item.pressure_level === 0 ? 1 : item.pressure_level

					// 降水確率のコンバート
					// CHANGE: 10％単位の表記にそろえた
					const rainChance = item.rain_chance ? `${Math.round(item.rain_chance / 10) * 10}%` : '-'

					// 湿度のコンバート
					const humidity = item.humidity ? `${Math.round(item.humidity)}%` : '-'

					// 風速のコンバート（仕様は全国マップから流用）
					const roundWind = item.wind_v > 0 ? Math.round(Number(item.wind_v)) : -1	// 無風は-1としてwindLevelを0として扱う
					let windLevel = 0
					switch (true) {
						case roundWind >= 15:
							windLevel = 4
							break
						case roundWind >= 10:
							windLevel = 3
							break
						case roundWind >= 5:
							windLevel = 2
							break
						case roundWind >= 0:
							windLevel = 1
							break
					}

					// 風向きのコンバート（風向きは16方位(北から時計回りに16：北、1：北北東、2：北東～15：北北西となる。)が入っている。実装は全国マップから流用）
					const windRotation = (Number(item.wind_d) - 1) * (360 / 16) + 180	// NOTE: 画像はデフォルトで北北東（1）を向いている
					// console.log(item.wind_d)
					// console.log(windRotation)

					let windD = ''
					switch (Number(item.wind_d)) {
						case 16:
							windD = '北'
							break
						case 0:	// 風速なし
							windD = '静穏'
							break
						case 1:
							windD = '北北東'
							break
						case 2:
							windD = '北東'
							break
						case 3:
							windD = '東北東'
							break
						case 4:
							windD = '東'
							break
						case 5:
							windD = '東南東'
							break
						case 6:
							windD = '南東'
							break
						case 7:
							windD = '南南東'
							break
						case 8:
							windD = '南'
							break
						case 9:
							windD = '南南西'
							break
						case 10:
							windD = '南西'
							break
						case 11:
							windD = '西南西'
							break
						case 12:
							windD = '西'
							break
						case 13:
							windD = '西北西'
							break
						case 14:
							windD = '北西'
							break
						case 15:
							windD = '北北西'
							break
					}
					// if (windD === '') console.log(item)

					const index = date.diff(nowDate, 'days')	// 今日との差分をindexとする
					data[index].data.push({
						// 'city_code': item.city_code,
						'city_name': item.city_name,
						'hour': hour,
						'is_now_hour': isNowHour,
						// 'is_past_hour': isPastHour,
						'humidity': humidity,
						'pressure': item.pressure,
						// 'pressure_diff': item.pressure_diff,
						'pressure_level': pressureLevel,
						'rain_chance': rainChance,
						'rain_fall': Math.round(item.rain_fall),
						// 'rain_in_span': item.rain_in_span,
						'temperature': Math.round(item.temperature),
						'weather_code': weatherName,
						'wind_level': windLevel,
						'wind_rotation': windRotation,
						'wind_v': Math.round(item.wind_v),
						'wind_d': windD
					})
				}
			}
			// console.log('computedWeatherData: ', data)

			return data
		}
	},
	methods: {
		// map Vuex actions
		...mapActions('weatherGraph', [SET_WEATHER_DATA]),
		...mapActions('weatherForecast', [SET_WEATHER_FORECAST, SET_SOLAR_TIME]),

		// 必要なデータをAPIリクエストする処理
		getWeatherData () {
			const today = moment()
			const tomorrow = moment().add(1, 'days')
			const dayAfterTommorrow = moment().add(2, 'days')
			// CHANGE: 過去時刻が非表示になったため、現在時刻から取得するよう変更
			// const from = today.set({ 'hour': 0, 'minute': 0, 'second': 0, 'millisecond': 0 }).unix()
			const from = today.unix()
			const to = dayAfterTommorrow.set({ 'hour': 23, 'minute': 59, 'second': 59, 'millisecond': 999 }).unix()
			// console.log(today.format('YYYYMMDD HH:mm:SS'))
			// console.log(tomorrow.format('YYYYMMDD HH:mm:SS'))
			// console.log(dayAfterTommorrow.format('YYYYMMDD HH:mm:SS'))
			// console.log(from)
			// console.log(to)

			// 日単位の気象データ取得API
			const SET_WEATHER_FORECAST = this.SET_WEATHER_FORECAST({
				user_id: this.userId,
				user_token: this.userToken,
				from: from,
				to: to,
				city_code: this.cityCode
			})

			// 時単位の気象データ取得API
			const SET_WEATHER_DATA = this.SET_WEATHER_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				from: from,
				to: to,
				include_rise_warning: 1,	// 上昇アラートフラグを立てる
				city_code: this.cityCode
			})

			this.isLoading = true
			Promise.all([SET_WEATHER_FORECAST, SET_WEATHER_DATA]).then(res => {
				// console.log('SET_WEATHER_FORECAST, SET_WEATHER_DATA: ', res)
				// API通信でOKでもエラーになるケース
				if (this.weatherForecastData.records && this.weatherForecastData.records.length !== 3) {
					this.isError = true	// 日単位データが3日分ないケース
					throw new Error('200 OK Error: There is no daily data for 3 days.')
				} else if (!this.weatherData.records) {
					this.isError = true	// 時単位データがないケース
					throw new Error('200 OK Error: Not found weatherData.records')
				} else if (this.weatherData.records && this.weatherData.records.length < 49) {
					this.isError = true	// 時単位データが2日分+1時間（24*2+1）ないケース
					throw new Error('200 OK Error: There is no hourly data for 72 hours.')
				} else {
					// 今日の日の出・日の入り
					const SET_SOLAR_TIME_TODAY = this.SET_SOLAR_TIME({
						user_id: this.userId,
						user_token: this.userToken,
						year: today.year(),
						month: today.month() + 1,
						day: today.date(),
						city_code: this.cityCode
					})

					// 明日の日の出・日の入り
					const SET_SOLAR_TIME_TOMORROW = this.SET_SOLAR_TIME({
						user_id: this.userId,
						user_token: this.userToken,
						year: tomorrow.year(),
						month: tomorrow.month() + 1,
						day: tomorrow.date(),
						city_code: this.cityCode
					})

					// 明後日の日の出・日の入り
					const SET_SOLAR_TIME_DAYAFTERTOMORROW = this.SET_SOLAR_TIME({
						user_id: this.userId,
						user_token: this.userToken,
						year: dayAfterTommorrow.year(),
						month: dayAfterTommorrow.month() + 1,
						day: dayAfterTommorrow.date(),
						city_code: this.cityCode
					})

					// 日の出・日の入りのリクエスト
					return Promise.all([SET_SOLAR_TIME_TODAY, SET_SOLAR_TIME_TOMORROW, SET_SOLAR_TIME_DAYAFTERTOMORROW])
				}
			}).then(res => {
				// console.log('SET_SOLAR_TIME_TODAY, SET_SOLAR_TIME_TOMORROW, SET_SOLAR_TIME_DAYAFTERTOMORROW: ', res)
				// 日の出・日の入りデータ作成
				this.arraySolarTime = [res[0].data, res[1].data, res[2].data].map(item => {
					return {
						'sunrise': item.sunrise,
						'sunrise_hour': Number(item.sunrise.split(':')[0]),
						'sunset': item.sunset,
						'sunset_hour': Number(item.sunset.split(':')[0])
					}
				})
				// console.log(this.arraySolarTime)
				this.$nextTick(() => {
					// 初期化
					this.init()

					// イベント追加
					this.addEvent()

					// 現在時刻へスクロール
					this.scrollHandler()
				})
			}).catch(error => {
				// NOTE: エラー処理
				console.log('Error: ', error)
				this.isError = true
			}).finally(() => {
				this.isLoading = false
			})
		},

		// 初期化
		init () {
			// DOMの設定
			this.$elemWrapper = document.getElementById('js_weatherForecast')
			const $elemInner = document.getElementById('js_weatherForecastInner')
			this.$elemDay = document.getElementById('js_weatherForecastDay')
			this.$elemHour = document.getElementById('js_weatherForecastHour')
			this.$elemScrollPointer = document.getElementById('js_weatherForecastHour_scrollPointer')

			// w320以上の横幅は拡大処理
			const propWidth = document.body.offsetWidth
			this.zoom = propWidth / 320
			$elemInner.setAttribute('style', `transform: translateX(-50%) scale(${this.zoom}, ${this.zoom}); -webkit-transform: translateX(-50%) scale(${this.zoom}, ${this.zoom});`)

			// スクロール領域の最大横幅
			this.widthWeatherForecastHour = this.$elemHour.scrollWidth - this.$elemHour.offsetWidth

			// 時単位スクロールで日単位を切り替える横位置を設定
			const headerWidth = 30
			this.posToday = document.getElementById('js_tomorrowHour-title').getBoundingClientRect().left - headerWidth
			this.posTomorrow = document.getElementById('js_tomorrowHour-18').getBoundingClientRect().left - headerWidth
			this.posDayAfterTomorrow = document.getElementById('js_dayaftertomorrowHour-title').getBoundingClientRect().left - headerWidth
			// console.log(this.posToday)
			// console.log(this.posTomorrow)
			// console.log(this.posDayAfterTomorrow)

			// VueScrollToオブジェクト生成
			this.scrollerDay = scroller()
			this.scrollerHour = scroller()
		},

		// スクロールをコントロールするハンドラ
		scrollHandler (id, isAutoScrollHour) {
			// console.log(`scrollHandler: ${typeof id === 'undefined' ? 'now' : id}`)
			const now = moment().format('H')
			switch (id) {
				case 0:
					// 時単位の現在時刻へスクロールする処理
					// if (isAutoScrollHour) this.scrollToHour(`js_todayHour-title`, 'js_weatherForecastHour')	// 今日の見出しにスクロールするならこちら
					if (isAutoScrollHour) this.scrollToHour(`js_todayHour-${now}`, 'js_weatherForecastHour')
					// 高速スクロールで前段階を飛ばされたケースは今日へスクロールする処理
					if (this.viewDateType > 1) this.scrollToDay('js_today', 'js_weatherForecastDay')
					break
				case 1:
					// 時単位の明日見出しへスクロールする処理
					if (isAutoScrollHour) this.scrollToHour(`js_tomorrowHour-title`, 'js_weatherForecastHour')
					// 日単位の今日へスクロールする処理
					if (this.viewDateType !== 1) this.scrollToDay('js_today', 'js_weatherForecastDay')
					break
				case 2:
					// 時単位の明日18時へスクロールする処理
					if (isAutoScrollHour) this.scrollToHour(`js_tomorrowHour-18`, 'js_weatherForecastHour')
					// 日単位の明日へスクロールする処理
					if (this.viewDateType !== 2) this.scrollToDay('js_tomorrow', 'js_weatherForecastDay')
					break
				case 3:
					// 時単位の明後日見出しへスクロールする処理
					if (isAutoScrollHour) this.scrollToHour(`js_dayaftertomorrowHour-title`, 'js_weatherForecastHour')
					// 高速スクロールで前段階を飛ばされたケースは日単位の明日へスクロールする処理
					if (this.viewDateType < 2) this.scrollToDay('js_tomorrow', 'js_weatherForecastDay')
					break
				default:
					// 現在時刻へスクロールする処理
					this.scrollToHour(`js_todayHour-${now}`, 'js_weatherForecastHour')
					break
			}
			this.viewDateType = !id ? 0 : id
		},

		// 日単位のスクロール処理
		scrollToDay (id, parentId) {
			// console.log('scrollTo:', `${id}-${parentId}`)
			const elem = document.getElementById(id)
			const options = {
				container: `#${parentId}`,
				duration: 200,
				easing: 'ease-out',
				offset: 0,
				force: true,
				cancelable: true,
				onStart: false,
				onDone: false,
				onCancel: false,
				x: true,
				y: false
			}
			this.scrollerDay(elem, options)
		},

		// 時単位のスクロール処理
		scrollToHour (id, parentId) {
			// console.log('scrollTo:', `${id}-${parentId}`)
			const elem = document.getElementById(id)
			const duration = 200
			const _this = this
			const options = {
				container: `#${parentId}`,
				duration: duration,
				easing: 'ease-out',
				offset: -30,	// 見出し分の余白
				force: true,
				cancelable: false,
				onStart: () => { this.isAutoScroll = true },
				onDone: () => {
					setTimeout(() => {
						_this.isAutoScroll = false
					}, duration)
				},
				onCancel: false,
				x: true,
				y: false
			}
			this.scrollerHour(elem, options)
		},

		// イベント登録
		addEvent () {
			// 日単位
			this.$elemDay.addEventListener('touchstart', this.onSwipeStart)
			this.$elemDay.addEventListener('touchmove', this.onSwipeMove)
			this.$elemDay.addEventListener('touchend', this.onSwipeEnd)

			// 時単位
			this.$elemHour.addEventListener('scroll', this.onScroll)
		},

		// 時単位のスクロールイベント
		onScroll (e) {
			let left = e.target.scrollLeft
			const pos = left / this.widthWeatherForecastHour * 100
			this.$elemScrollPointer.setAttribute('style', `left: ${pos}%;`)
			left = left * this.zoom
			// console.log(this.isAutoScroll)
			// console.log(e)
			// console.log(left)

			if (!this.isAutoScroll) {	// 自動スクロール中は何もしない
				// const POS_TODAY = 936
				// const POS_TOMORROW = 1612
				// const POS_DAY_AFTER_TOMORROW = 1875
				switch (true) {
					case left < this.posToday:
						this.scrollHandler(0, false)
						break
					case left >= this.posToday && left < this.posTomorrow:
						this.scrollHandler(1, false)
						break
					case left >= this.posTomorrow && left < this.posDayAfterTomorrow:
						this.scrollHandler(2, false)
						break
					case left >= this.posDayAfterTomorrow:
						this.scrollHandler(3, false)
						break
				}
			}
		},

		// 日単位の日付変更イベント
		onChangeDate (e, clickDateType) {
			e.preventDefault()

			// console.log('onChangeDate')
			// console.log(clickDateType)
			switch (clickDateType) {
				case 'today':
					// console.log('onChangeDate today')
					this.scrollHandler(0, true)
					break
				case 'tomorrow':
					switch (this.viewDateType) {
						case 0:
							// console.log('onChangeDate tomorrow: viewDateType 0')
							this.scrollHandler(1, true)
							break
						case 1:
							// console.log('onChangeDate tomorrow: viewDateType 1')
							this.scrollHandler(2, true)
							break
						case 2:
							// console.log('onChangeDate tomorrow: viewDateType 2')
							this.scrollHandler(1, true)
							break
						case 3:
							// console.log('onChangeDate tomorrow: viewDateType 3')
							this.scrollHandler(2, true)
							break
					}
					break
				case 'dayaftertomorrow':
					// console.log('onChange dayaftertomorrow')
					this.scrollHandler(3, true)
					break
			}
		},

		// スワイプ開始イベント
		onSwipeStart (e) {
			// NOTE: イベントキャンセルするとクリックイベントが取れなくなる
			this.startX = e.touches[0].pageX
			this.isSwipe = false	// フラグを初期化
		},

		// スワイプ移動イベント
		onSwipeMove (e) {
			e.preventDefault()	// スワイプの場合、イベントキャンセルしてクリックイベント停止
			this.endX = e.touches[0].pageX
			this.isSwipe = true	// スワイプフラグを立てる
		},

		// スワイプ終了イベント
		onSwipeEnd (e) {
			if (!this.isSwipe) return false	// スワイプでなければ何もしない

			e.preventDefault()
			const diff = this.endX - this.startX
			if (diff > 0 && this.viewDateType !== 0) {	// 右にスワイプ且つ今日をアクティブ表示していない場合
				// console.log('onSwipeEnd: 右向き')
				this.scrollHandler(this.viewDateType - 1, true)
			} else if (diff < 0 && this.viewDateType !== 3) {	// 左にスワイプ且つ明後日をアクティブ表示していない場合
				// console.log('onSwipeEnd: 左向き')
				this.scrollHandler(this.viewDateType + 1, true)
			}
		},

		// トグルボタンクリックイベント
		onToggle (e) {
			e.preventDefault()

			this.isAccordionClose = !this.isAccordionClose
			const paddingTop = this.isAccordionClose ? 100 : 134.375
			this.$elemWrapper.setAttribute('style', `padding-top: ${paddingTop}%;`)
		}
	},
	beforeDestroy () {
		// イベントの破棄
		if (this.$elemDay) {
			// console.log('remove')
			this.$elemDay.removeEventListener('touchstart', this.onSwipeStart)
			this.$elemDay.removeEventListener('touchmove', this.onSwipeMove)
			this.$elemDay.removeEventListener('touchend', this.onSwipeEnd)
		}
		if (this.$elemHour) this.$elemHour.removeEventListener('scroll', this.onScroll)
	}
}
</script>

<style lang="scss" scoped>
@import "../../assets/sass/variable";
// NNOTE: 定額デザインシステム外
$color-sunday: #FF4E00;
$color-saturday:  #55B1FF;
$color-red: #FF9F5C;
$color-blue: #59C2FF;
$color-grey: #E6E6E6;
$color-darkgrey: #999999;
$color-pressure-lv1: #9FF8FF;
$color-pressure-lv2: #CAE8A5;
$color-pressure-lv3: #FFD486;
$color-pressure-lv4: #F7A6AC;
$color-pressure-lv6: #B3BCFF;
$color-today: #FFAAAA;
$font-size-11: 0.687rem;
$spacing-1: 0.062rem;
$spacing-7: 0.437rem;
$spacing-12: 0.75rem;
$spacing-15: 0.937rem;
$active-width: 68.18%;
$passive-width: 31.82%;
$duration: 1.2s;
$duration-short: .2s;
$easing: ease-out;

.weather-forecast {
	position: relative;

	/deep/ .redraw-progress { top: 0; }

	&__content {
		position: relative;
		padding-top: 100%;
		background-color: $background-primary;
		transition: padding-top $duration-short $easing;
	}

	// エラー
	&__error {
		padding-top: 98.125%;
		overflow: hidden;

		&--bg {
			position: absolute;
			width: 100%;
			top: 0;
			left: 0;
			margin: 0;

			& > img { width: 100%; }
		}
	}

	// ローディング
	/deep/ .webview-redraw-progress {
		background: url(~@/assets/img/weather_forecast_bg.svg) left bottom / 100% auto no-repeat;

		&::before {
			content: '';
			display: block;
			width: 100%;
			height: 100%;
			position: absolute;
			left: 0;
			top: 0;
			background: rgba($_black, .4);
		}
	}

	&__inner {
		position: absolute;
		// height: 320px;
		width: 320px;
		top: 0;
		left: 50%;
		margin: 0 auto;
		transform: translateX(-50%);
		transform-origin: center 0;
		overflow: hidden;
	}

	// 日単位天気予報
	&-day {
		font-family: $lp-family-primary;
		display: flex;
		height: 162px;
		width: 100%;
		box-sizing: border-box;
		overflow: hidden;
		padding: $spacing-15 0 $spacing-8;

		&__inner {
			position: relative;
			display: flex;
			align-items: center;
			flex-direction: column;
			// overflow: hidden;
			box-sizing: border-box;
			padding: 0 $spacing-6 0;
			min-width: $active-width;	// アクティブ横幅

			&:nth-of-type(n+2)::before {
				content: '';
				display: block;
				position: absolute;
				height: 100%;
				width: 4px;
				top: 0;
				left: -2px;
				background-color: $background-list;
				border-radius: 2px;
			}

			animation: fadeActiveWidth $duration $easing 0s 1 normal;
		}

		// 表示する日によってはみ出す区切り線を隠す
		&.is-view-date-type2 > .is-tomorrow.is-active::before,
		&.is-view-date-type3 > .is-tomorrow.is-passive::before {
			content: none;
		}

		// アクティブ
		&__title {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 100%;
			margin: 0;
			font-weight: normal;
			font-size: $font-size-12;

			&--fixed {
				font-family: $lp-family-tertiary;
				text-align: center;
				letter-spacing: 0.04em;
			}

			&--variable {
				display: inline-block;
				text-align: right;
				white-space: nowrap;
				// 以下、アニメーション変更部分
				max-width: 77.6059%;
				opacity: 1;
				overflow: hidden;
				transition: opacity $duration-short $easing, max-width $duration-short $easing;

				& > span {
					display: inline-block;
					padding-left: $spacing-6;
					vertical-align: top;
				}

				& > .is-date {
					letter-spacing: 0.04em;

					& > .is-sunday { color: $color-sunday; }
					& > .is-saturday { color: $color-saturday; }
				}
			}
			.is-red { color: $color-red; }
			.is-blue { color: $color-blue; }
		}

		&__weather {
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: stretch;
			width: 100%;
			margin: $spacing-4 auto 0;
			overflow: hidden;
			animation: fadeActiveForcast $duration $easing 0s 1 normal;
		}

		&__icon { padding-right: $spacing-4; }

		&__forecast {
			&--tenki {
				margin: $spacing-12 0 0;
				font-family: $lp-family-tertiary;
				font-size: $font-size-14;
				line-height: $line-height-1;
			}

			&--temp {
				display: flex;
				margin: $spacing-8 0 0;
				font-family: $family-primary;
				font-size: $font-size-10;
				line-height: $line-height-1;
				white-space: nowrap;

				& > p:first-of-type { margin: 0; }
				& > p:last-of-type { margin: 0 0 0 $spacing-4; }
				& > .is-temp-max { color: $color-red; }
				& > .is-temp-min { color: $color-blue; }
				.is-large { font-size: $font-size-16; }
			}

			&--precipitation {
				margin: $spacing-6 0 0;
				font-size: $font-size-10;
				color: $color-blue;
				line-height: $line-height-1;

				img { padding-bottom: $spacing-1; }

				.is-large {
					margin-left: $spacing-4;
					font-size: $font-size-16;
				}
			}
		}

		&__pressure {
			position: absolute;
			bottom: 0;
			left: 50%;
			transform: translateX(-50%);

			& > i {
				transition: transform $duration-short $easing;
			}
		}

		// 非アクティブ
		&__inner.is-passive {
			min-width: $passive-width;
			animation: fadePassiveWidth $duration $easing 0s 1 normal;

			.is-hide { display: none; }

			.weather-forecast-day {
				&__title {
					&--fixed { font-family: $lp-family-primary; }
					&--variable {
						max-width: 0px;
						opacity: 0;
					}
				}

				&__weather {
					flex-direction: column;
					margin: 0;
					animation: fadePassiveForcast $duration $easing 0s 1 normal;
				}

				&__icon {
					padding: 0;

					& > i {
						margin: 0 auto;
						transform: scale(.8);
					}
				}

				&__forecast {
					margin: -$spacing-6 auto 0;
					text-align: center;
					font-size: $font-size-9;

					.is-large { font-size: $font-size-14; }

					&--temp { margin: 0 auto; }
					&--precipitation {
						margin: $spacing-4 0 0;
						img { width: 5px; }
					}
				}

				&__pressure > i {
					transform: scale(.6429);
					transform-origin: 50% bottom;
				}
			}
		}
	}

	// 時単位天気予報
	&-hour {
		// 時単位セルサイズ用の変数
		$line-height-cell: 1.2;
		$height-time: 24px;
		$height-temperature: 35px;
		$height-pressure: 46px;
		$height-rainy: 43px;
		$height-humidity: 22px;
		$height-wind: 46px;
		$height-temperature-icon: 22px;
		$width-cell: 35px;

		font-family: $lp-family-primary;
		position: relative;
		padding: $spacing-6 0 0;
		overflow: hidden;
		box-sizing: border-box;
		transition: height $duration-short $easing;
		line-height: $line-height-cell;

		// スクロールバー
		&__scroll-bar {
			$scroll-bar-width: 60px;
			$scroll-bar-height: 4px;
			$scroll-bar-spacing: 4px;

			position: relative;
			width: calc(100% - #{$scroll-bar-width} - (#{$scroll-bar-spacing} * 2));	// 横幅からスクロールバーの長さと余白を引いたものがスクロール領域
			height: $scroll-bar-height;
			margin: 0 0 0 $scroll-bar-spacing;

			&--point {
				display: block;
				position: absolute;
				width: $scroll-bar-width;
				height: $scroll-bar-height;
				border-radius: 4px;
				top: 50%;
				left: 0;
				transform: translateY(-50%);
				background-color: $color-grey;
			}
		}

		&__inner {
			display: flex;
			height: $height-time + $height-temperature + $height-pressure + $height-rainy + $height-humidity + $height-wind;
			margin: $spacing-6 0 0;
			font-size: $font-size-10;
			white-space: nowrap;
			transition: height $duration-short $easing;
			overflow-x: scroll;
			overflow-y: hidden;

			&::-webkit-scrollbar {
				display: none;
				-webkit-appearance: none;
			}

			// 閉じたとき
			&.is-close {
				height: $height-time + $height-temperature + $height-pressure;
				ul > li:nth-child(n + 4) {
					opacity: 0;
				}
			}
		}

		// セルの設定
		&__header--time,
		&__data--time {
			height: $height-time;
		}

		&__header--temperature,
		&__data--temperature {
			height: $height-temperature;
		}

		&__header--pressure,
		&__data--pressure {
			height: $height-pressure;
		}

		&__header--rainy,
		&__data--rainy {
			height: $height-rainy;
			margin-top: $spacing-4;
		}

		&__header--humidity,
		&__data--humidity {
			height: $height-humidity;
		}

		&__header--wind,
		&__data--wind {
			height: $height-wind;
		}

		&__data--solor-time {
			padding-top: calc(#{$height-temperature-icon} / 2);
		}

		ul {
			list-style: none;
			padding: 0;
			margin: 0;
		}

		li {
			position: relative;
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;
			opacity: 1;
			transition: opacity $duration-short $easing;

			& > p { width: calc(100% - 2px); }
		}

		// 見出し
		&__header {
			position: sticky;
			top: 0;
			left: 0;
			width: 30px;
			padding-top: $spacing-4 !important;
			flex-shrink: 0;
			box-sizing: border-box;
			text-align: center;
			background-color: $background-primary;
			font-size: $font-size-9;
			z-index: 10;

			i { margin: 0 auto; }
			p { margin: 0; }
		}

		// データ
		&__data {
			display: flex;

			&--inner {
				display: flex;
			}

			&--container {
				display: flex;
			}

			&--date {
				width: 28px;
				margin: 0;
				padding-top: $spacing-7;
				font-size: $font-size-9;
				text-align: center;

				&::after {
					content: '';
					display: block;
					width: 4px;
					height: calc(100% - 15px);	// 文字の高さを引く
					margin: 0 auto;
					background-color: $color-grey;
					border-radius: 4px;
				}
			}

			&--item {
				width: $width-cell;
				text-align: center;

				i { margin: 0 auto; }
				p { margin: 0; }
			}

			&--time.is-now-hour {
				position: relative;
				font-family: $lp-family-tertiary;

				&::after {
					content: '';
					display: block;
					position: absolute;
					bottom: 1px;
					left: 50%;
					width: 88.24%;
					height: 4px;
					transform: translateX(-50%);
					border-radius: 4px;
					background-color: $color-today;
				}
			}

			&--temperature {
				font-size: $font-size-11;
				color: $color-red;
			}

			&--pressure {
				p[class^="is-pressure-lv"] {
					margin-top: -$spacing-4;
					padding: 0 0 $spacing-2;
					border-radius: 4px;
					line-height: $line-height-1;
					font-size: $font-size-9;
					text-align: center;
				}

				.is-pressure-lv1 { background-color: $color-pressure-lv1; }
				.is-pressure-lv2 { background-color: $color-pressure-lv2; }
				.is-pressure-lv3 { background-color: $color-pressure-lv3; }
				.is-pressure-lv4 { background-color: $color-pressure-lv4; }
				.is-pressure-lv6 { background-color: $color-pressure-lv6; }
			}

			&--rainy {
				font-size: $font-size-9;

				.is-rain_chance {
					position: relative;
					height: 30px;
					width: calc(100% - 1px);
					vertical-align: bottom;

					& > span {
						position: absolute;
						bottom: 2px;
						left: 50%;
						transform: translateX(-50%);
					}
				}
			}

			&--wind > i.sprite_weather_forcast_hour.wind_arrow_lv0 {	// 無風のレイアウト
				background: none;
				width: 16px;
				height: 16px;
			}

			&--solor-time {
				font-size: $font-size-9;

				& > p {
					margin-top: $spacing-2;
					line-height: $line-height-1;
				}
			}
		}

		// トグルボタン
		&__toggle {
			padding: $spacing-10 0;
			margin: 0 auto;
			text-align: center;
			box-sizing: border-box;
			line-height: $line-height-1;

			&--button {
				width: 80px;
				border: none;
				background-color: $color-darkgrey;
				border-radius: 8px;
				line-height: $line-height-1;

				ons-icon {
					margin: -$spacing-2 0 0;
					color: $text-seconday;
					font-size: $font-size-14;
				}
			}
		}
	}
}

// アニメーション
@keyframes fadePassiveWidth {
	0%   { min-width: $active-width; }
	20%  { min-width: $passive-width; }
	100% { min-width: $passive-width; }
}

@keyframes fadeActiveWidth {
	0%   { min-width: $passive-width; }
	20%  { min-width: $active-width; }
	100% { min-width: $active-width; }
}

@keyframes fadePassiveForcast {
	0%   { opacity: 0; }
	20%  { opacity: 1; }
	100% { opacity: 1; }
}

@keyframes fadeActiveForcast {
	0%   { opacity: 0; }
	20%  { opacity: 1; }
	100% { opacity: 1; }
}

.fade-enter-active, .fade-leave-active {
	transition: opacity .2s;
}
.fade-enter, .fade-leave-to {
	opacity: 0;
}

// スプライト
.sprite_weather_forcast_hour {	// 時単位
	background-image: url(~@/assets/img/sprite_weather_forcast_hour.png);
	background-size: (78px / 2) (1250px / 2);
	background-repeat: no-repeat;
	display: block;

	&.icon_hour {
		width: (24px / 2);
		height: (24px / 2);
		background-position: (-5px / 2) (-5px / 2);
	}

	&.icon_humidity {
		width: (24px / 2);
		height: (24px / 2);
		background-position: (-5px / 2) (-39px / 2);
	}

	&.icon_pressure {
		width: (24px / 2);
		height: (24px / 2);
		background-position: (-5px / 2) (-73px / 2);
	}

	&.icon_rain {
		width: (24px / 2);
		height: (24px / 2);
		background-position: (-5px / 2) (-107px / 2);
	}

	&.icon_temperature {
		width: (24px / 2);
		height: (24px / 2);
		background-position: (-5px / 2) (-141px / 2);
	}

	&.icon_wind {
		width: (24px / 2);
		height: (24px / 2);
		background-position: (-5px / 2) (-175px / 2);
	}

	&.pressure_icon_lv1 {
		width: (68px / 2);
		height: (68px / 2);
		background-position: (-5px / 2) (-209px / 2);
	}

	&.pressure_icon_lv2 {
		width: (68px / 2);
		height: (68px / 2);
		background-position: (-5px / 2) (-287px / 2);
	}

	&.pressure_icon_lv3 {
		width: (68px / 2);
		height: (68px / 2);
		background-position: (-5px / 2) (-365px / 2);
	}

	&.pressure_icon_lv4 {
		width: (68px / 2);
		height: (68px / 2);
		background-position: (-5px / 2) (-443px / 2);
	}

	&.pressure_icon_lv6 {
		width: (68px / 2);
		height: (68px / 2);
		background-position: (-5px / 2) (-521px / 2);
	}

	&.wicon_ame {
		width: (44px / 2);
		height: (44px / 2);
		background-position: (-5px / 2) (-599px / 2);
	}

	&.wicon_hare {
		width: (44px / 2);
		height: (44px / 2);
		background-position: (-5px / 2) (-653px / 2);
	}

	&.wicon_haremax {
		width: (44px / 2);
		height: (44px / 2);
		background-position: (-5px / 2) (-707px / 2);
	}

	&.wicon_haremoon {
		width: (44px / 2);
		height: (44px / 2);
		background-position: (-5px / 2) (-761px / 2);
	}

	&.wicon_kumori {
		width: (44px / 2);
		height: (44px / 2);
		background-position: (-5px / 2) (-815px / 2);
	}

	&.wicon_sunrise {
		width: (44px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-869px / 2);
	}

	&.wicon_sunset {
		width: (44px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-951px / 2);
	}

	&.wicon_yuki {
		width: (44px / 2);
		height: (44px / 2);
		background-position: (-5px / 2) (-1033px / 2);
	}

	&.wind_arrow_lv1 {
		width: (32px / 2);
		height: (32px / 2);
		background-position: (-5px / 2) (-1087px / 2);
	}

	&.wind_arrow_lv2 {
		width: (32px / 2);
		height: (32px / 2);
		background-position: (-5px / 2) (-1129px / 2);
	}

	&.wind_arrow_lv3 {
		width: (32px / 2);
		height: (32px / 2);
		background-position: (-5px / 2) (-1171px / 2);
	}

	&.wind_arrow_lv4 {
		width: (32px / 2);
		height: (32px / 2);
		background-position: (-5px / 2) (-1213px / 2);
	}
}

.sprite_weather_forcast_day {	// 日単位
	background-image: url(~@/assets/img/sprite_weather_forcast_day.png);
	background-size: (290px / 2) (4490px / 2);
	background-repeat: no-repeat;
	display: block;

	&.pressure_icon_lv1 {
		width: (280px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-5px / 2);
	}

	&.pressure_icon_lv2 {
		width: (280px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-87px / 2);
	}

	&.pressure_icon_lv3 {
		width: (280px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-169px / 2);
	}

	&.pressure_icon_lv4 {
		width: (280px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-251px / 2);
	}

	&.pressure_icon_lv5 {
		width: (280px / 2);
		height: (72px / 2);
		background-position: (-5px / 2) (-333px / 2);
	}

	&.tenki_100 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-415px / 2);
	}

	&.tenki_101 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-551px / 2);
	}

	&.tenki_103 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-687px / 2);
	}

	&.tenki_105 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-823px / 2);
	}

	&.tenki_111 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-959px / 2);
	}

	&.tenki_114 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1095px / 2);
	}

	&.tenki_117 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1231px / 2);
	}

	&.tenki_200 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1367px / 2);
	}

	&.tenki_201 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1503px / 2);
	}

	&.tenki_202 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1639px / 2);
	}

	&.tenki_203 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1775px / 2);
	}

	&.tenki_204 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-1911px / 2);
	}

	&.tenki_205 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2047px / 2);
	}

	&.tenki_211 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2183px / 2);
	}

	&.tenki_214 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2319px / 2);
	}

	&.tenki_217 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2455px / 2);
	}

	&.tenki_300 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2591px / 2);
	}

	&.tenki_301 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2727px / 2);
	}

	&.tenki_302 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2863px / 2);
	}

	&.tenki_303 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-2999px / 2);
	}

	&.tenki_311 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3135px / 2);
	}

	&.tenki_313 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3271px / 2);
	}

	&.tenki_315 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3407px / 2);
	}

	&.tenki_400 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3543px / 2);
	}

	&.tenki_401 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3679px / 2);
	}

	&.tenki_402 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3815px / 2);
	}

	&.tenki_403 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-3951px / 2);
	}

	&.tenki_411 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-4087px / 2);
	}

	&.tenki_413 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-4223px / 2);
	}

	&.tenki_414 {
		width: (120px / 2);
		height: (126px / 2);
		background-position: (-5px / 2) (-4359px / 2);
	}
}
</style>
